import { useQuery } from '@tanstack/react-query';
import { projects } from '@/api/projects';
import type { ProjectKind } from '@/api/projects';

interface Props {
  value: string | null;
  onChange: (value: string | null) => void;
  organizationId?: string;
  disabled?: boolean;
  kind?: ProjectKind;
}

/**
 * Селектор напряму/проекта для редакторів документів.
 * - Без kind: дві групи (Напрямки, Проєкти) — обирається будь-яке.
 * - kind='DIRECTION': лише наскрізні напрямки.
 * - kind='PROJECT': лише разові проєкти.
 */
export default function ProjectSelect({ value, onChange, organizationId, disabled, kind }: Props) {
  const projectsQ = useQuery({
    queryKey: ['projects-active', organizationId ?? '', kind ?? 'ALL'],
    // Власник 2026-06-02: запит з organization_id='' → 422.
    // Запит має сенс лише коли org обрана.
    enabled: !!organizationId,
    queryFn: () => projects.list({
      organization_id: organizationId,
      status: 'ACTIVE',
      kind,
      limit: 500,
    }),
  });

  const items = projectsQ.data?.items ?? [];
  const directions = items.filter((p) => p.kind === 'DIRECTION');
  const projectsOnly = items.filter((p) => p.kind === 'PROJECT');

  return (
    <select
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || null)}
      disabled={disabled}
    >
      <option value="">
        {kind === 'DIRECTION' ? '— Без напряму —' : kind === 'PROJECT' ? '— Без проєкту —' : '— Без напряму/проєкту —'}
      </option>
      {kind === 'DIRECTION' || kind === undefined ? (
        <optgroup label="Напрямки (наскрізні)">
          {directions.map((p) => (
            <option key={p.id} value={p.id}>
              {p.code} — {p.name}
            </option>
          ))}
        </optgroup>
      ) : null}
      {kind === 'PROJECT' || kind === undefined ? (
        <optgroup label="Проєкти (тимчасові)">
          {projectsOnly.map((p) => (
            <option key={p.id} value={p.id}>
              {p.code} — {p.name}
            </option>
          ))}
        </optgroup>
      ) : null}
    </select>
  );
}
